import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Alert, AlertDescription } from "./ui/alert";
import { 
  Loader2, 
  Plus, 
  Trash2, 
  Package, 
  Truck, 
  Clock, 
  DollarSign,
  FileText,
  Save,
  Check,
  Zap,
  Calculator,
  Printer,
  Share2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Cores Primaza - Verde
const colors = {
  primaza: {
    primary: '#0d9f4f',      // Verde principal
    primaryDark: '#0a7a3d',  // Verde escuro
    primaryLight: '#e8f8ef', // Verde claro (fundo)
    secondary: '#f5a623',    // Laranja/amarelo
  }
};

const CotacaoOperacional = ({ user }) => {
  // Estados do formulário
  const [pedidoNumero, setPedidoNumero] = useState('');
  const [cepDestino, setCepDestino] = useState('');
  const [valorNF, setValorNF] = useState('');
  const [pesoTotal, setPesoTotal] = useState('');
  const [enderecoDestino, setEnderecoDestino] = useState(null);
  const [cpfCnpjDestino, setCpfCnpjDestino] = useState('');
  
  // Volumes/Caixas - apenas dimensões e quantidade
  const [volumes, setVolumes] = useState([
    { id: 1, altura: '', largura: '', comprimento: '', quantidade: '1', caixaSelecionada: '' }
  ]);
  
  // Caixas Padrão
  const [caixasPadrao, setCaixasPadrao] = useState([]);
  
  // Estados de UI
  const [loading, setLoading] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);
  const [error, setError] = useState('');
  const [cotacoes, setCotacoes] = useState([]);
  const [cotacaoSelecionada, setCotacaoSelecionada] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [cotacaoSalva, setCotacaoSalva] = useState(null);
  
  // Histórico
  const [historico, setHistorico] = useState([]);
  const [showHistorico, setShowHistorico] = useState(false);

  // Buscar CEP
  const buscarCep = async (cep) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;
    
    setLoadingCep(true);
    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      if (response.data && !response.data.erro) {
        setEnderecoDestino(response.data);
        setError('');
      } else {
        setEnderecoDestino(null);
        setError('CEP não encontrado');
      }
    } catch (err) {
      setEnderecoDestino(null);
      setError('Erro ao buscar CEP');
    } finally {
      setLoadingCep(false);
    }
  };

  // Calcular cubagem de um volume (em m³)
  // Entrada em CENTÍMETROS, saída em m³
  const calcularCubagem = (altura, largura, comprimento, quantidade) => {
    const a = parseFloat(altura) || 0;  // cm
    const l = parseFloat(largura) || 0;  // cm
    const c = parseFloat(comprimento) || 0;  // cm
    const q = parseInt(quantidade) || 1;
    // Converter cm³ para m³: dividir por 1.000.000
    return (a * l * c * q) / 1000000;
  };

  // Carregar caixas padrão
  const carregarCaixasPadrao = async () => {
    try {
      const token = localStorage.getItem('primaza_token');
      const response = await axios.get(`${API}/caixas-padrao`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCaixasPadrao(response.data || []);
    } catch (err) {
      console.error('Erro ao carregar caixas padrão:', err);
    }
  };

  // Selecionar caixa padrão para um volume
  const selecionarCaixa = (volumeId, caixaId) => {
    if (!caixaId) {
      // Limpar seleção
      setVolumes(volumes.map(v => 
        v.id === volumeId ? { ...v, caixaSelecionada: '', altura: '', largura: '', comprimento: '' } : v
      ));
      return;
    }
    
    const caixa = caixasPadrao.find(c => c.id === caixaId);
    if (caixa) {
      setVolumes(volumes.map(v => 
        v.id === volumeId ? { 
          ...v, 
          caixaSelecionada: caixaId,
          altura: caixa.altura.toString(),
          largura: caixa.largura.toString(),
          comprimento: caixa.comprimento.toString()
        } : v
      ));
    }
  };

  // Adicionar volume
  const adicionarVolume = () => {
    const novoId = Math.max(...volumes.map(v => v.id)) + 1;
    setVolumes([...volumes, { id: novoId, altura: '', largura: '', comprimento: '', quantidade: '1', caixaSelecionada: '' }]);
  };

  // Remover volume
  const removerVolume = (id) => {
    if (volumes.length > 1) {
      setVolumes(volumes.filter(v => v.id !== id));
    }
  };

  // Atualizar volume (com opção de limpar seleção de caixa)
  const atualizarVolume = (id, campo, valor, limparCaixa = false) => {
    setVolumes(volumes.map(v => {
      if (v.id === id) {
        const novoVolume = { ...v, [campo]: valor };
        if (limparCaixa) {
          novoVolume.caixaSelecionada = '';
        }
        return novoVolume;
      }
      return v;
    }));
  };

  // Normalizar valor decimal (aceita vírgula ou ponto)
  const parseDecimal = (value) => {
    if (!value) return 0;
    // Substituir vírgula por ponto para parseFloat funcionar
    const normalized = String(value).replace(',', '.');
    return parseFloat(normalized) || 0;
  };

  // Calcular totais
  const calcularTotais = () => {
    let cubagemTotal = 0;
    let quantidadeTotal = 0;
    
    volumes.forEach(v => {
      const cubagem = calcularCubagem(v.altura, v.largura, v.comprimento, v.quantidade);
      cubagemTotal += cubagem;
      quantidadeTotal += parseInt(v.quantidade) || 1;
    });
    
    return { 
      pesoTotal: parseDecimal(pesoTotal), 
      cubagemTotal: cubagemTotal,
      quantidadeTotal 
    };
  };

  // Calcular cotações
  const calcularCotacoes = async (e) => {
    e.preventDefault();
    setError('');
    setCotacoes([]);
    setCotacaoSelecionada(null);
    setCotacaoSalva(null);
    
    if (!enderecoDestino) {
      setError('Digite um CEP válido');
      return;
    }
    
    const { pesoTotal: peso, cubagemTotal } = calcularTotais();
    if (peso <= 0) {
      setError('Informe o peso total');
      return;
    }
    
    setLoading(true);
    
    try {
      const cepLimpo = cepDestino.replace(/\D/g, '');
      const valorNFNum = parseDecimal(valorNF);
      const cpfCnpjLimpo = cpfCnpjDestino.replace(/\D/g, '');
      const tipoPessoa = cpfCnpjLimpo.length === 11 ? 'F' : 'J';
      
      // Chamar todas as APIs em paralelo
      const [resESM, resBraspress, resCorreios, resRodonaves] = await Promise.all([
        // Expresso São Miguel - com CPF/CNPJ para cotação precisa
        axios.post(`${API}/esm/cotacao`, {
          cep_destino: cepLimpo,
          peso: peso,
          cubagem: cubagemTotal || 0.1,
          valor_mercadoria: valorNFNum,
          quantidade: volumes.reduce((acc, v) => acc + (parseInt(v.quantidade) || 1), 0),
          tipo_frete: 'CIF',
          cnpj_cpf_destino: cpfCnpjLimpo || undefined,
          tipo_pessoa_destino: cpfCnpjLimpo ? tipoPessoa : 'J'
        }).catch(err => ({ data: null, error: err })),
        
        // Braspress
        axios.post(`${API}/braspress/cotacao-simples`, {
          cep_destino: cepLimpo,
          peso: peso,
          valor_mercadoria: valorNFNum,
          volumes: volumes.reduce((acc, v) => acc + (parseInt(v.quantidade) || 1), 0),
          tipo_frete: 'CIF'
        }).catch(err => ({ data: null, error: err })),
        
        // Correios - dimensões já estão em cm, enviar diretamente
        axios.post(`${API}/correios/cotacao`, {
          cep_destino: cepLimpo,
          peso: peso,
          comprimento: Math.max(...volumes.map(v => parseFloat(v.comprimento) || 20)),
          altura: Math.max(...volumes.map(v => parseFloat(v.altura) || 10)),
          largura: Math.max(...volumes.map(v => parseFloat(v.largura) || 15)),
          valor_declarado: valorNFNum
        }).catch(err => ({ data: null, error: err })),
        
        // Rodonaves - com volumes detalhados
        axios.post(`${API}/rodonaves/cotacao`, {
          cep_destino: cepLimpo,
          peso: peso,
          valor_mercadoria: valorNFNum,
          cnpj_cpf_destinatario: cpfCnpjLimpo || undefined,
          volumes: volumes.map(v => ({
            altura: parseFloat(v.altura) || 20,
            largura: parseFloat(v.largura) || 20,
            comprimento: parseFloat(v.comprimento) || 30,
            quantidade: parseInt(v.quantidade) || 1
          }))
        }).catch(err => ({ data: null, error: err }))
      ]);
      
      let todasCotacoes = [];
      
      // ESM
      if (resESM.data?.success && resESM.data?.valor_frete) {
        let prazoDias = 1;
        if (resESM.data.previsao_entrega && resESM.data.previsao_embarque) {
          const [diaE, mesE, anoE] = resESM.data.previsao_entrega.split(' ')[0].split('/');
          const [diaB, mesB, anoB] = resESM.data.previsao_embarque.split('/');
          prazoDias = Math.max(1, Math.ceil((new Date(anoE, mesE-1, diaE) - new Date(anoB, mesB-1, diaB)) / 86400000));
        }
        
        // Capturar taxas extras
        const taxasExtras = [];
        if (resESM.data.valor_tda && resESM.data.valor_tda > 0) {
          taxasExtras.push({ nome: 'TDA', valor: resESM.data.valor_tda });
        }
        if (resESM.data.valor_tas && resESM.data.valor_tas > 0) {
          taxasExtras.push({ nome: 'TAS', valor: resESM.data.valor_tas });
        }
        if (resESM.data.valor_tde && resESM.data.valor_tde > 0) {
          taxasExtras.push({ nome: 'TDE', valor: resESM.data.valor_tde });
        }
        if (resESM.data.valor_gris && resESM.data.valor_gris > 0) {
          taxasExtras.push({ nome: 'GRIS', valor: resESM.data.valor_gris });
        }
        if (resESM.data.valor_pedagio && resESM.data.valor_pedagio > 0) {
          taxasExtras.push({ nome: 'Pedágio', valor: resESM.data.valor_pedagio });
        }
        
        // Calcular frete base (total - taxas)
        const totalTaxas = taxasExtras.reduce((sum, t) => sum + t.valor, 0);
        const freteBase = resESM.data.valor_frete_base || (resESM.data.valor_frete - totalTaxas);
        
        todasCotacoes.push({
          id: 'esm',
          transportadora: 'Expresso São Miguel',
          valor: resESM.data.valor_frete,
          prazo: prazoDias,
          previsao_entrega: resESM.data.previsao_entrega,
          detalhes: resESM.data.informacao_entrega,
          // Taxas extras detalhadas
          taxas: taxasExtras,
          freteBase: freteBase > 0 ? freteBase : null,
          interior: resESM.data.interior
        });
      }
      
      // Braspress
      if (resBraspress.data?.success && resBraspress.data?.valor_frete) {
        todasCotacoes.push({
          id: 'braspress',
          transportadora: 'Braspress',
          valor: resBraspress.data.valor_frete,
          prazo: resBraspress.data.prazo_dias
        });
      }
      
      // Correios
      if (resCorreios.data?.success && resCorreios.data?.cotacoes) {
        resCorreios.data.cotacoes.forEach(cot => {
          if (cot.valor_frete && cot.valor_frete > 0) {
            // Capturar taxas extras dos Correios
            const taxasExtras = [];
            if (cot.valor_ar && cot.valor_ar > 0) {
              taxasExtras.push({ nome: 'AR', valor: cot.valor_ar });
            }
            if (cot.valor_vd && cot.valor_vd > 0) {
              taxasExtras.push({ nome: 'VD', valor: cot.valor_vd });
            }
            
            todasCotacoes.push({
              id: `correios_${cot.servico}`,
              transportadora: `Correios ${cot.nome_servico}`,
              valor: cot.valor_frete,
              prazo: cot.prazo_dias,
              detalhes: cot.detalhes,
              taxas: taxasExtras,
              freteBase: cot.valor_base || (cot.valor_frete - taxasExtras.reduce((s, t) => s + t.valor, 0))
            });
          }
        });
      }
      
      // Rodonaves
      if (resRodonaves.data?.success && resRodonaves.data?.valor_frete) {
        todasCotacoes.push({
          id: 'rodonaves',
          transportadora: 'Rodonaves',
          valor: resRodonaves.data.valor_frete,
          prazo: 5, // Prazo médio padrão Rodonaves
          detalhes: resRodonaves.data.protocolo ? `Protocolo: ${resRodonaves.data.protocolo}` : null,
          taxas: [],
          freteBase: resRodonaves.data.valor_frete
        });
      }
      
      // Ordenar por prazo (mais rápido primeiro)
      todasCotacoes.sort((a, b) => a.prazo - b.prazo);
      
      // Marcar mais rápido e mais barato
      if (todasCotacoes.length > 0) {
        todasCotacoes[0].maisRapido = true;
        const maisBarato = todasCotacoes.reduce((min, c) => c.valor < min.valor ? c : min, todasCotacoes[0]);
        maisBarato.maisBarato = true;
      }
      
      setCotacoes(todasCotacoes);
      
      if (todasCotacoes.length === 0) {
        setError('Nenhuma transportadora disponível para esta rota');
      }
    } catch (err) {
      console.error('Erro:', err);
      setError('Erro ao calcular cotações');
    } finally {
      setLoading(false);
    }
  };

  // Salvar cotação no histórico
  const salvarCotacao = async () => {
    if (!cotacaoSelecionada) {
      setError('Selecione uma transportadora');
      return;
    }
    
    setSalvando(true);
    
    try {
      const { pesoTotal: peso, cubagemTotal, quantidadeTotal } = calcularTotais();
      
      const dadosCotacao = {
        pedido_numero: pedidoNumero || null,
        cep_destino: cepDestino.replace(/\D/g, ''),
        cidade_destino: enderecoDestino?.localidade,
        uf_destino: enderecoDestino?.uf,
        valor_nf: parseDecimal(valorNF),
        peso_total: peso,
        cubagem_total: cubagemTotal,
        quantidade_volumes: quantidadeTotal,
        volumes: volumes.map(v => ({
          altura: parseFloat(v.altura) || 0,
          largura: parseFloat(v.largura) || 0,
          comprimento: parseFloat(v.comprimento) || 0,
          quantidade: parseInt(v.quantidade) || 1,
          cubagem: calcularCubagem(v.altura, v.largura, v.comprimento, v.quantidade)
        })),
        todas_cotacoes: cotacoes,
        transportadora_selecionada: cotacaoSelecionada.transportadora,
        valor_frete_selecionado: cotacaoSelecionada.valor,
        prazo_selecionado: cotacaoSelecionada.prazo,
        usuario: user?.username
      };
      
      const response = await axios.post(`${API}/cotacao-historico/salvar`, dadosCotacao);
      
      if (response.data.success) {
        setCotacaoSalva(response.data);
        setError('');
        carregarHistorico();
      }
    } catch (err) {
      console.error('Erro ao salvar:', err);
      setError(err.response?.data?.detail || 'Erro ao salvar cotação');
    } finally {
      setSalvando(false);
    }
  };

  // Carregar histórico
  const carregarHistorico = async () => {
    try {
      const response = await axios.get(`${API}/cotacao-historico`);
      setHistorico(response.data || []);
    } catch (err) {
      console.error('Erro ao carregar histórico:', err);
    }
  };

  // Gerar PDF/Comprovante
  const gerarPDF = async (cotacaoId) => {
    const novaJanela = window.open('', '_blank');
    
    if (!novaJanela) {
      setError('Popup bloqueado. Permita popups para gerar o comprovante.');
      return;
    }
    
    novaJanela.document.write('<html><body style="font-family: Arial; text-align: center; padding-top: 100px;"><h2 style="color: #0d9f4f;">Carregando comprovante...</h2></body></html>');
    
    try {
      const response = await axios.get(`${API}/cotacao-historico/${cotacaoId}/pdf`, {
        responseType: 'text'
      });
      
      novaJanela.document.open();
      novaJanela.document.write(response.data);
      novaJanela.document.close();
    } catch (err) {
      console.error('Erro ao gerar PDF:', err);
      novaJanela.document.open();
      novaJanela.document.write(`<html><body style="font-family: Arial; text-align: center; padding-top: 100px;"><h2 style="color: red;">Erro ao carregar comprovante</h2><p>${err.response?.data?.detail || err.message}</p></body></html>`);
      novaJanela.document.close();
      setError('Erro ao gerar comprovante');
    }
  };

  // Gerar PDF de Comparação (sem salvar) - Para negociação com transportadoras
  const gerarPDFComparacao = () => {
    if (cotacoes.length === 0) {
      setError('Faça uma cotação primeiro');
      return;
    }

    const novaJanela = window.open('', '_blank');
    
    if (!novaJanela) {
      setError('Popup bloqueado. Permita popups para gerar o PDF.');
      return;
    }

    const { pesoTotal, cubagemTotal, quantidadeTotal } = calcularTotais();
    const dataHoje = new Date().toLocaleDateString('pt-BR');
    const horaHoje = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

    // Gerar tabela de cotações
    let cotacoesHTML = '';
    const cotacoesOrdenadas = [...cotacoes].sort((a, b) => a.valor - b.valor);
    const menorValor = cotacoesOrdenadas[0]?.valor || 0;
    
    cotacoesOrdenadas.forEach((cot, idx) => {
      const isMelhor = idx === 0;
      const diferenca = cot.valor - menorValor;
      const diferencaPercent = menorValor > 0 ? ((diferenca / menorValor) * 100).toFixed(1) : 0;
      
      cotacoesHTML += `
        <tr style="${isMelhor ? 'background-color: #e8f8ef; font-weight: 600;' : ''}">
          <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">${cot.transportadora}</td>
          <td style="padding: 10px; border-bottom: 1px solid #e5e7eb; text-align: right; font-weight: 600; color: ${isMelhor ? '#0d9f4f' : '#333'};">R$ ${cot.valor.toFixed(2)}</td>
          <td style="padding: 10px; border-bottom: 1px solid #e5e7eb; text-align: center;">${cot.prazo} dias</td>
          <td style="padding: 10px; border-bottom: 1px solid #e5e7eb; text-align: right; color: ${diferenca > 0 ? '#dc2626' : '#0d9f4f'};">
            ${isMelhor ? '✓ MELHOR' : `+R$ ${diferenca.toFixed(2)} (+${diferencaPercent}%)`}
          </td>
        </tr>
      `;
    });

    // Gerar tabela de volumes
    let volumesHTML = '';
    volumes.forEach((vol, idx) => {
      const cub = calcularCubagem(vol.altura, vol.largura, vol.comprimento, vol.quantidade);
      volumesHTML += `
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #e5e7eb; text-align: center;">${idx + 1}</td>
          <td style="padding: 8px; border-bottom: 1px solid #e5e7eb; text-align: center;">${vol.altura || 0}</td>
          <td style="padding: 8px; border-bottom: 1px solid #e5e7eb; text-align: center;">${vol.largura || 0}</td>
          <td style="padding: 8px; border-bottom: 1px solid #e5e7eb; text-align: center;">${vol.comprimento || 0}</td>
          <td style="padding: 8px; border-bottom: 1px solid #e5e7eb; text-align: center;">${vol.quantidade || 1}</td>
          <td style="padding: 8px; border-bottom: 1px solid #e5e7eb; text-align: right;">${cub.toFixed(4)} m³</td>
        </tr>
      `;
    });

    const html = `
    <!DOCTYPE html>
    <html lang="pt-BR">
    <head>
      <meta charset="UTF-8">
      <title>Comparativo de Fretes - Primaza</title>
      <style>
        @page { size: A4; margin: 15mm 20mm; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; font-size: 11pt; color: #333; line-height: 1.4; }
        .header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 15px; border-bottom: 3px solid #0d9f4f; margin-bottom: 20px; }
        .header-left { flex: 1; }
        .company-name { font-size: 22pt; font-weight: 700; color: #0d9f4f; }
        .company-subtitle { font-size: 9pt; color: #666; margin-top: 2px; }
        .header-right { text-align: right; }
        .doc-title { font-size: 14pt; font-weight: 700; color: #0a7a3d; text-transform: uppercase; }
        .doc-date { font-size: 10pt; color: #666; margin-top: 5px; }
        .section { margin-bottom: 20px; }
        .section-title { font-size: 12pt; font-weight: 600; color: #0d9f4f; margin-bottom: 10px; padding-bottom: 5px; border-bottom: 1px solid #e5e7eb; }
        .info-grid { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 15px; }
        .info-box { background: #f9fafb; padding: 12px; border-radius: 6px; border-left: 3px solid #0d9f4f; }
        .info-label { font-size: 9pt; color: #666; margin-bottom: 3px; }
        .info-value { font-size: 12pt; font-weight: 600; color: #0d9f4f; }
        table { width: 100%; border-collapse: collapse; }
        th { background: #0d9f4f; color: white; padding: 10px; text-align: left; font-size: 10pt; }
        .highlight { background: #fff3cd; padding: 15px; border-radius: 6px; border-left: 4px solid #f5a623; margin-top: 20px; }
        .highlight-title { font-weight: 600; color: #856404; margin-bottom: 5px; }
        .highlight-text { font-size: 10pt; color: #856404; }
        .footer { margin-top: 30px; padding-top: 15px; border-top: 1px solid #e5e7eb; font-size: 9pt; color: #666; text-align: center; }
        @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="header-left">
          <div class="company-name">PRIMAZA</div>
          <div class="company-subtitle">Termoplásticos Ltda</div>
        </div>
        <div class="header-right">
          <div class="doc-title">Comparativo de Fretes</div>
          <div class="doc-date">${dataHoje} às ${horaHoje}</div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Dados do Envio</div>
        <div class="info-grid">
          <div class="info-box">
            <div class="info-label">Destino</div>
            <div class="info-value">${enderecoDestino?.localidade || '-'}/${enderecoDestino?.uf || '-'}</div>
            <div style="font-size: 9pt; color: #666;">CEP: ${cepDestino || '-'}</div>
          </div>
          <div class="info-box">
            <div class="info-label">Valor da Nota</div>
            <div class="info-value">R$ ${parseDecimal(valorNF).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
          </div>
          <div class="info-box">
            <div class="info-label">Peso Total</div>
            <div class="info-value">${pesoTotal.toFixed(2)} kg</div>
          </div>
          <div class="info-box">
            <div class="info-label">Cubagem Total</div>
            <div class="info-value">${cubagemTotal.toFixed(4)} m³</div>
          </div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Volumes (${quantidadeTotal} unidades)</div>
        <table>
          <thead>
            <tr>
              <th style="text-align: center;">#</th>
              <th style="text-align: center;">Alt (cm)</th>
              <th style="text-align: center;">Larg (cm)</th>
              <th style="text-align: center;">Prof (cm)</th>
              <th style="text-align: center;">Qtd</th>
              <th style="text-align: right;">Cubagem</th>
            </tr>
          </thead>
          <tbody>
            ${volumesHTML}
          </tbody>
        </table>
      </div>

      <div class="section">
        <div class="section-title">Comparativo de Transportadoras (ordenado por valor)</div>
        <table>
          <thead>
            <tr>
              <th>Transportadora</th>
              <th style="text-align: right;">Valor Frete</th>
              <th style="text-align: center;">Prazo</th>
              <th style="text-align: right;">Comparação</th>
            </tr>
          </thead>
          <tbody>
            ${cotacoesHTML}
          </tbody>
        </table>
      </div>

      <div class="highlight">
        <div class="highlight-title">💡 Use este comparativo para negociação!</div>
        <div class="highlight-text">
          Envie para outras transportadoras solicitando cobertura de preço.<br>
          Melhor oferta atual: <strong>${cotacoesOrdenadas[0]?.transportadora || '-'}</strong> com <strong>R$ ${(cotacoesOrdenadas[0]?.valor || 0).toFixed(2)}</strong>
        </div>
      </div>

      <div class="footer">
        <p>Documento gerado automaticamente pelo Sistema Primaza • ${dataHoje} ${horaHoje}</p>
        <p>Valores sujeitos a confirmação no momento da contratação</p>
      </div>

      <script>
        window.onload = function() {
          setTimeout(function() { window.print(); }, 500);
        };
      </script>
    </body>
    </html>
    `;

    novaJanela.document.open();
    novaJanela.document.write(html);
    novaJanela.document.close();
  };

  useEffect(() => {
    carregarHistorico();
    carregarCaixasPadrao();
  }, []);

  const totais = calcularTotais();

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: colors.primaza.primary }}>
            Cotação Operacional
          </h1>
          <p className="text-gray-500 text-sm">
            Calcule fretes detalhados e selecione a melhor opção
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => setShowHistorico(!showHistorico)}
          style={{ borderColor: colors.primaza.primary, color: colors.primaza.primary }}
        >
          <FileText size={16} className="mr-2" />
          {showHistorico ? 'Nova Cotação' : 'Histórico'}
        </Button>
      </div>

      {showHistorico ? (
        /* Histórico de Cotações */
        <Card>
          <CardHeader style={{ borderBottom: `3px solid ${colors.primaza.primary}` }}>
            <CardTitle style={{ color: colors.primaza.primary }}>Histórico de Cotações</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {historico.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Nenhuma cotação salva ainda</p>
            ) : (
              <div className="space-y-3">
                {historico.map((item, index) => (
                  <div 
                    key={item.id || index}
                    className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                    style={{ borderLeft: `4px solid ${colors.primaza.primary}` }}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium" style={{ color: colors.primaza.primary }}>
                          {item.transportadora_selecionada}
                          {item.pedido_numero && (
                            <span className="ml-2 text-sm text-gray-500">
                              Pedido: {item.pedido_numero}
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-gray-600">
                          {item.cidade_destino}/{item.uf_destino} • {item.peso_total?.toFixed(2)}kg • {item.cubagem_total?.toFixed(4)}m³
                        </p>
                        <p className="text-xs text-gray-400">
                          {new Date(item.data_cotacao).toLocaleString('pt-BR')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold" style={{ color: colors.primaza.primary }}>
                          R$ {item.valor_frete_selecionado?.toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500">{item.prazo_selecionado} dias</p>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => gerarPDF(item.id)}
                          className="mt-1"
                          style={{ color: colors.primaza.primary }}
                        >
                          <FileText size={14} className="mr-1" />
                          Comprovante
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Formulário de Cotação */
        <>
          {/* Dados do Envio */}
          <Card className="mb-6">
            <CardHeader className="pb-3" style={{ borderBottom: `3px solid ${colors.primaza.primary}` }}>
              <CardTitle className="text-lg flex items-center gap-2" style={{ color: colors.primaza.primary }}>
                <Truck size={20} />
                Dados do Envio
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="pedido">Nº Pedido (opcional)</Label>
                  <Input
                    id="pedido"
                    placeholder="Ex: 40839"
                    value={pedidoNumero}
                    onChange={(e) => setPedidoNumero(e.target.value)}
                  />
                </div>
                
                <div>
                  <Label htmlFor="cep">CEP Destino *</Label>
                  <Input
                    id="cep"
                    placeholder="00000-000"
                    value={cepDestino}
                    onChange={(e) => {
                      setCepDestino(e.target.value);
                      if (e.target.value.replace(/\D/g, '').length === 8) {
                        buscarCep(e.target.value);
                      }
                    }}
                  />
                  {loadingCep && <p className="text-xs text-gray-400 mt-1">Buscando...</p>}
                  {enderecoDestino && (
                    <p className="text-xs mt-1" style={{ color: colors.primaza.primary }}>
                      {enderecoDestino.localidade}/{enderecoDestino.uf}
                    </p>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="valorNF">Valor da Nota *</Label>
                  <Input
                    id="valorNF"
                    type="text"
                    inputMode="decimal"
                    placeholder="0,00"
                    value={valorNF}
                    onChange={(e) => {
                      // Permitir apenas números, vírgula e ponto
                      const val = e.target.value.replace(/[^0-9.,]/g, '');
                      setValorNF(val);
                    }}
                  />
                </div>

                <div>
                  <Label htmlFor="pesoTotal">Peso Bruto Total (kg) *</Label>
                  <Input
                    id="pesoTotal"
                    type="text"
                    inputMode="decimal"
                    placeholder="0,000"
                    value={pesoTotal}
                    onChange={(e) => {
                      // Permitir apenas números, vírgula e ponto
                      const val = e.target.value.replace(/[^0-9.,]/g, '');
                      setPesoTotal(val);
                    }}
                  />
                </div>

                <div>
                  <Label htmlFor="cpfCnpj">CPF/CNPJ Destinatário</Label>
                  <Input
                    id="cpfCnpj"
                    placeholder="Opcional - para cotação precisa"
                    value={cpfCnpjDestino}
                    onChange={(e) => setCpfCnpjDestino(e.target.value)}
                    className="font-mono"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Preencha para verificar taxas de interior (TDA/TAS)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Calculadora de Cubagem */}
          <Card className="mb-6">
            <CardHeader className="pb-3" style={{ borderBottom: `3px solid ${colors.primaza.primary}` }}>
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg flex items-center gap-2" style={{ color: colors.primaza.primary }}>
                  <Calculator size={20} />
                  Calculadora de Cubagem
                </CardTitle>
                <p className="text-sm text-gray-500">
                  Medidas em <strong>centímetros</strong>
                </p>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              {/* Tabela de volumes */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ backgroundColor: colors.primaza.primaryLight }}>
                      <th className="p-2 text-left font-medium" style={{ color: colors.primaza.primary }}>Caixa Padrão</th>
                      <th className="p-2 text-left font-medium" style={{ color: colors.primaza.primary }}>Altura (cm)</th>
                      <th className="p-2 text-left font-medium" style={{ color: colors.primaza.primary }}>Largura (cm)</th>
                      <th className="p-2 text-left font-medium" style={{ color: colors.primaza.primary }}>Profund. (cm)</th>
                      <th className="p-2 text-left font-medium" style={{ color: colors.primaza.primary }}>Qtd</th>
                      <th className="p-2 text-left font-medium" style={{ color: colors.primaza.primary }}>Cubagem (m³)</th>
                      <th className="p-2 text-center font-medium" style={{ color: colors.primaza.primary }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {volumes.map((volume, index) => {
                      const cubagem = calcularCubagem(volume.altura, volume.largura, volume.comprimento, volume.quantidade);
                      return (
                        <tr key={volume.id} className="border-b">
                          <td className="p-2">
                            <select
                              value={volume.caixaSelecionada || ''}
                              onChange={(e) => selecionarCaixa(volume.id, e.target.value)}
                              className="h-8 w-44 text-sm border rounded-md px-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                              style={{ borderColor: '#d1d5db' }}
                            >
                              <option value="">-- Manual --</option>
                              {caixasPadrao.map(caixa => (
                                <option key={caixa.id} value={caixa.id}>
                                  {caixa.nome} ({caixa.comprimento}×{caixa.largura}×{caixa.altura})
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="p-2">
                            <Input
                              type="number"
                              step="1"
                              placeholder="0"
                              value={volume.altura}
                              onChange={(e) => atualizarVolume(volume.id, 'altura', e.target.value, true)}
                              className="h-8 w-20"
                            />
                          </td>
                          <td className="p-2">
                            <Input
                              type="number"
                              step="1"
                              placeholder="0"
                              value={volume.largura}
                              onChange={(e) => atualizarVolume(volume.id, 'largura', e.target.value, true)}
                              className="h-8 w-20"
                            />
                          </td>
                          <td className="p-2">
                            <Input
                              type="number"
                              step="1"
                              placeholder="0"
                              value={volume.comprimento}
                              onChange={(e) => atualizarVolume(volume.id, 'comprimento', e.target.value, true)}
                              className="h-8 w-20"
                            />
                          </td>
                          <td className="p-2">
                            <Input
                              type="number"
                              min="1"
                              step="1"
                              placeholder="1"
                              value={volume.quantidade}
                              onChange={(e) => atualizarVolume(volume.id, 'quantidade', e.target.value)}
                              className="h-8 w-16"
                            />
                          </td>
                          <td className="p-2 font-medium" style={{ color: colors.primaza.primary }}>
                            {cubagem.toFixed(4)}
                          </td>
                          <td className="p-2 text-center">
                            {volumes.length > 1 ? (
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => removerVolume(volume.id)}
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 size={16} />
                              </Button>
                            ) : (
                              <span className="text-gray-300">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              <Button 
                size="sm" 
                variant="outline" 
                onClick={adicionarVolume}
                className="mt-3"
                style={{ borderColor: colors.primaza.primary, color: colors.primaza.primary }}
              >
                <Plus size={16} className="mr-1" />
                Adicionar
              </Button>

              {/* Totalizadores */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 p-4 rounded-lg" style={{ backgroundColor: colors.primaza.primaryLight }}>
                <div>
                  <p className="text-xs text-gray-500">Valor da Nota</p>
                  <p className="text-lg font-semibold" style={{ color: colors.primaza.primary }}>
                    R$ {parseDecimal(valorNF).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Peso Bruto Total</p>
                  <p className="text-lg font-semibold" style={{ color: colors.primaza.primary }}>
                    {totais.pesoTotal.toFixed(2)} kg
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">M³ Total</p>
                  <p className="text-lg font-semibold" style={{ color: colors.primaza.primary }}>
                    {totais.cubagemTotal.toFixed(4)} m³
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Quantidade</p>
                  <p className="text-lg font-semibold" style={{ color: colors.primaza.primary }}>
                    {totais.quantidadeTotal} {totais.quantidadeTotal === 1 ? 'volume' : 'volumes'}
                  </p>
                </div>
              </div>
              
              <Button 
                className="w-full mt-4 h-12 text-base"
                onClick={calcularCotacoes}
                disabled={loading}
                style={{ backgroundColor: colors.primaza.primary }}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Calculando...
                  </>
                ) : (
                  <>
                    <DollarSign size={20} className="mr-2" />
                    Gerar Cotações
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </>
      )}

      {/* Erro */}
      {error && (
        <Alert variant="destructive" className="mt-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Resultados */}
      {cotacoes.length > 0 && !showHistorico && (
        <Card className="mt-6">
          <CardHeader className="pb-3" style={{ borderBottom: `3px solid ${colors.primaza.primary}` }}>
            <CardTitle className="text-lg" style={{ color: colors.primaza.primary }}>
              Opções de Frete ({cotacoes.length})
            </CardTitle>
            <p className="text-sm text-gray-500">
              Ordenado por prazo de entrega (mais rápido primeiro)
            </p>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-3">
              {cotacoes.map((cotacao, index) => (
                <div
                  key={cotacao.id}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    cotacaoSelecionada?.id === cotacao.id
                      ? 'bg-green-50'
                      : 'hover:border-gray-300'
                  }`}
                  style={{
                    borderColor: cotacaoSelecionada?.id === cotacao.id ? colors.primaza.primary : '#e5e7eb'
                  }}
                  onClick={() => setCotacaoSelecionada(cotacao)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold">{cotacao.transportadora}</p>
                        {cotacao.maisRapido && (
                          <span className="text-xs text-white px-2 py-0.5 rounded-full flex items-center gap-1" style={{ backgroundColor: colors.primaza.secondary }}>
                            <Zap size={10} />
                            Mais Rápido
                          </span>
                        )}
                        {cotacao.maisBarato && (
                          <span className="text-xs text-white px-2 py-0.5 rounded-full" style={{ backgroundColor: colors.primaza.primary }}>
                            Mais Barato
                          </span>
                        )}
                        {cotacao.interior && (
                          <span className="text-xs text-white px-2 py-0.5 rounded-full bg-orange-500">
                            Interior
                          </span>
                        )}
                      </div>
                      {cotacao.detalhes && (
                        <p className="text-xs text-gray-500 mt-1">{cotacao.detalhes}</p>
                      )}
                      {cotacao.previsao_entrega && (
                        <p className="text-xs text-gray-500 mt-1">
                          <Clock size={12} className="inline mr-1" />
                          Entrega: {cotacao.previsao_entrega}
                        </p>
                      )}
                      {/* Detalhamento de taxas */}
                      {cotacao.taxas && cotacao.taxas.length > 0 && (
                        <div className="mt-2 p-2 bg-yellow-50 rounded text-xs">
                          <p className="font-medium text-yellow-800 mb-1">Composição do valor:</p>
                          <div className="space-y-0.5 text-yellow-700">
                            {cotacao.freteBase && (
                              <p>Frete Base: R$ {cotacao.freteBase.toFixed(2)}</p>
                            )}
                            {cotacao.taxas.map((taxa, i) => (
                              <p key={i}>+ {taxa.nome}: R$ {taxa.valor.toFixed(2)}</p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold" style={{ color: colors.primaza.primary }}>
                        R$ {cotacao.valor.toFixed(2)}
                      </p>
                      <p className="text-sm text-gray-500">
                        {cotacao.prazo} {cotacao.prazo === 1 ? 'dia útil' : 'dias úteis'}
                      </p>
                      {cotacao.taxas && cotacao.taxas.length > 0 && (
                        <p className="text-xs text-orange-600 mt-1">
                          Inclui {cotacao.taxas.map(t => t.nome).join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                  {cotacaoSelecionada?.id === cotacao.id && (
                    <div className="mt-3 pt-3 border-t flex items-center justify-between">
                      <span className="text-sm flex items-center gap-1" style={{ color: colors.primaza.primary }}>
                        <Check size={16} />
                        Selecionado
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Botões de Ação */}
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              {/* Botão PDF Comparativo - Sempre visível quando há cotações */}
              <Button 
                variant="outline"
                className="flex-1 h-12"
                onClick={gerarPDFComparacao}
                style={{ borderColor: colors.primaza.primary, color: colors.primaza.primary }}
                data-testid="btn-pdf-comparativo"
              >
                <Share2 size={16} className="mr-2" />
                Gerar PDF Comparativo
              </Button>
              
              {/* Botão Salvar - Só aparece quando selecionou uma transportadora */}
              {cotacaoSelecionada && (
                <Button 
                  className="flex-1 h-12"
                  onClick={salvarCotacao}
                  disabled={salvando}
                  style={{ backgroundColor: colors.primaza.primary }}
                  data-testid="btn-salvar-cotacao"
                >
                  {salvando ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save size={16} className="mr-2" />
                      Salvar e Gerar Comprovante
                    </>
                  )}
                </Button>
              )}
            </div>

            {/* Cotação Salva */}
            {cotacaoSalva && (
              <Alert className="mt-4" style={{ borderColor: colors.primaza.primary, backgroundColor: colors.primaza.primaryLight }}>
                <AlertDescription className="flex items-center justify-between">
                  <span style={{ color: colors.primaza.primaryDark }}>
                    <Check size={16} className="inline mr-1" />
                    Cotação salva com sucesso!
                  </span>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => gerarPDF(cotacaoSalva.id)}
                    style={{ borderColor: colors.primaza.primary, color: colors.primaza.primary }}
                  >
                    <FileText size={14} className="mr-1" />
                    Ver Comprovante
                  </Button>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CotacaoOperacional;
